import { Dialog } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ButtonComponent } from 'component/Button';
import { SelectComponent } from 'component/Select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { ReactComponent as FilterSvg } from 'assets/images/icons/filter.svg';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { getOptionsForClearance } from 'api/FilterApi';
import {
  getEoreDonorChoices,
  getEoreCustomerChoices,
  getNtsDonorChoices,
  getNtsCustomerChoices,
  getHaDonorChoices,
  getHaCustomerChoices,
  getClearanceTaskChoices,
  getClearanceTaskChoicesForWeekly,
  getHaReportChoices,
} from 'api/ChoicesApi';

export const FilterByStatusDialog = (props) => {
  const [open, setOpen] = useState(false);
  const [dialogOpenedFrom, setDialogOpenedFrom] = useState('donor');
  const [donorOptions, setDonorOptions] = useState([]);

  const [filterStates, setFilterStates] = useState({
    nts: {
      selectedFilters: [{ value: 'donor', label: 'Donor' }],
      selectedDonor: [],
      selectedCustomer: [],
    },
    ha: {
      selectedFilters: [{ value: 'donor', label: 'Donor' }],
      selectedDonor: [],
      selectedCustomer: [],
    },
    eore: {
      selectedFilters: [{ value: 'donor', label: 'Donor' }],
      selectedDonor: [],
      selectedCustomer: [],
    },
  });

  const filterType = props.filterType || 'nts';
  const selectedValues = filterStates[filterType] || filterStates.nts;

  useEffect(() => {
    if (props?.getCurrentCompleteFilters) {
      const currentFilters = props?.getCurrentCompleteFilters();

      if (currentFilters && Object?.keys(currentFilters)?.length > 0) {
        setFilterStates((prevState) => ({
          ...prevState,
          [filterType]: {
            ...prevState[filterType],
            selectedDonor: currentFilters?.selectedDonor || [],
            selectedCustomer: currentFilters?.selectedCustomer || [],
            selectedFilters: [{ value: 'donor', label: 'Donor' }],
          },
        }));
      }
    }
  }, [props?.filterType, props?.getCurrentCompleteFilters]);

  const buildQueryParams = () => {
    const params = {};

    if (props?.currentDates?.dateFrom) params.date_from = props?.currentDates?.dateFrom;
    if (props?.currentDates?.dateTo) params.date_to = props?.currentDates?.dateTo;

    const currentFilters = props?.getCurrentCompleteFilters
      ? props.getCurrentCompleteFilters()
      : {};

    if (currentFilters?.selectedTeam?.length > 0) {
      params.team_id = currentFilters?.selectedTeam?.map((t) => t.value || t);
    }
    if (currentFilters?.selectedLocalCommunity?.length > 0) {
      params.local_community = currentFilters?.selectedLocalCommunity?.map((c) => c.value || c);
    }
    if (currentFilters?.selectedPolygonName?.length > 0) {
      params.hazard_name = currentFilters?.selectedPolygonName?.map((p) => p.value || p);
    }
    if (currentFilters?.selectedStatus?.length > 0) {
      params.validation_status = currentFilters?.selectedStatus?.map((s) => s.value || s);
    }
    if (currentFilters?.selectedDonor?.length > 0) {
      params.donor = currentFilters?.selectedDonor?.map((d) => d.value || d);
    }
    if (currentFilters?.selectedCustomer?.length > 0) {
      params['task-customer'] = currentFilters?.selectedCustomer?.map((c) => c.value || c);
    }

    if (filterType === 'ha') {
      if (currentFilters?.selectedAreaType?.length > 0) {
        params.hazard_area_type = currentFilters?.selectedAreaType?.map((a) => a.value || a);
      }
      if (currentFilters?.selectedHaMfBacType?.length > 0) {
        params.hazard_type = currentFilters?.selectedHaMfBacType?.map((h) => h.value || h);
      }
    }

    if (filterType === 'nts') {
      if (currentFilters?.selectedType?.length > 0) {
        params.is_vbv = currentFilters?.selectedType?.map((t) => String(t.value === 'true'));
      }
    }

    if (filterType === 'eore') {
      if (currentFilters?.selectedSettlement?.length > 0) {
        params.settlement_name = currentFilters?.selectedSettlement?.map((s) => s.value || s);
      }
      if (currentFilters?.selectedTypeOfPlace?.length > 0) {
        params.place_type_imsma = currentFilters?.selectedTypeOfPlace?.map((p) => p.value || p);
      }
      if (currentFilters?.selectedTypeOfSession?.length > 0) {
        params.eore_type_imsma = currentFilters?.selectedTypeOfSession?.map((e) => e.value || e);
      }
      if (currentFilters?.selectedBeneficiaries?.min)
        params.beneficiaries_min = currentFilters?.selectedBeneficiaries?.min;
      if (currentFilters?.selectedBeneficiaries?.max)
        params.beneficiaries_max = currentFilters?.selectedBeneficiaries?.max;
    }

    return params;
  };

  const formatChoiceResponse = (response) => {
    let formattedData = [];
    if (response?.data) {
      if (Array?.isArray(response?.data?.results)) {
        formattedData = response?.data?.results
          .filter((item) => item !== null && item !== undefined && item !== '')
          .map((item) => ({
            value: item,
            label: item,
          }));
      } else if (Array?.isArray(response?.data)) {
        formattedData = response?.data
          .filter((item) => item !== null && item !== undefined && item !== '')
          .map((item) => ({
            value: item,
            label: item,
          }));
      }
    }
    return formattedData;
  };

  const fetchEoreOptions = async (choiceMode) => {
    if (filterType !== 'eore') return;

    try {
      const query = buildQueryParams();
      const response =
        choiceMode === 'customer'
          ? await getEoreCustomerChoices(query)
          : await getEoreDonorChoices(query);

      const formattedData = formatChoiceResponse(response);

      if (formattedData?.length === 0) {
        setDonorOptions([{ value: 'no-data', label: 'Немає даних', isDisabled: true }]);
      } else {
        setDonorOptions(formattedData);
      }
    } catch (error) {
      console.error('Error fetching EORE filter choices:', error);
      setDonorOptions([{ value: 'error', label: 'Немає даних', isDisabled: true }]);
    }
  };

  const fetchNtsHaOptions = async (choiceMode) => {
    if (filterType !== 'nts' && filterType !== 'ha') return;

    try {
      const query = buildQueryParams();
      let response;
      if (filterType === 'nts') {
        response =
          choiceMode === 'customer'
            ? await getNtsCustomerChoices(query)
            : await getNtsDonorChoices(query);
      } else {
        response =
          choiceMode === 'customer'
            ? await getHaCustomerChoices(query)
            : await getHaDonorChoices(query);
      }

      const formattedData = formatChoiceResponse(response);

      if (formattedData?.length === 0) {
        setDonorOptions([{ value: 'no-data', label: 'Немає даних', isDisabled: true }]);
      } else {
        setDonorOptions(formattedData);
      }
    } catch (error) {
      console.error('Error fetching report filter choices:', error);
      setDonorOptions([{ value: 'error', label: 'Немає даних', isDisabled: true }]);
    }
  };

  const handleClickOpen = (openedFrom) => {
    const choiceMode = openedFrom === 'customer' ? 'customer' : 'donor';
    setDialogOpenedFrom(choiceMode);

    const currentFilters = props?.getCurrentCompleteFilters?.() || {};

    setFilterStates((prevState) => ({
      ...prevState,
      [filterType]: {
        ...prevState[filterType],
        selectedDonor: currentFilters?.selectedDonor || [],
        selectedCustomer: currentFilters?.selectedCustomer || [],
        selectedFilters: [{ value: 'donor', label: 'Donor' }],
      },
    }));

    setOpen(true);
    fetchEoreOptions(choiceMode);
    fetchNtsHaOptions(choiceMode);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const applyFilters = () => {
    setOpen(false);
    if (!props?.onFiltersChange) return;

    if (filterType === 'eore') {
      if (dialogOpenedFrom === 'customer') {
        props.onFiltersChange({ selectedCustomer: selectedValues?.selectedCustomer });
      } else {
        props.onFiltersChange({ selectedDonor: selectedValues?.selectedDonor });
      }
      return;
    }

    const completeCurrentFilters = props?.getCurrentCompleteFilters
      ? props.getCurrentCompleteFilters()
      : {};
    if (dialogOpenedFrom === 'customer') {
      props.onFiltersChange({
        ...completeCurrentFilters,
        selectedCustomer: selectedValues?.selectedCustomer,
      });
    } else {
      props.onFiltersChange({
        ...completeCurrentFilters,
        selectedDonor: selectedValues?.selectedDonor,
      });
    }
  };

  const handleDialogValueSelection = (selected) => {
    const field = dialogOpenedFrom === 'customer' ? 'selectedCustomer' : 'selectedDonor';
    setFilterStates((prevState) => ({
      ...prevState,
      [filterType]: {
        ...prevState[filterType],
        [field]: selected,
      },
    }));
  };

  const resetFilteredData = () => {
    if (dialogOpenedFrom === 'customer') {
      setFilterStates((prevState) => ({
        ...prevState,
        [filterType]: {
          ...prevState[filterType],
          selectedCustomer: [],
          selectedFilters: [{ value: 'donor', label: 'Donor' }],
        },
      }));
      if (props?.onFiltersChange) {
        if (filterType === 'eore') {
          props.onFiltersChange({ selectedCustomer: [] });
        } else {
          const completeCurrentFilters = props?.getCurrentCompleteFilters
            ? props.getCurrentCompleteFilters()
            : {};
          props.onFiltersChange({
            ...completeCurrentFilters,
            selectedCustomer: [],
          });
        }
      }
    } else {
      setFilterStates((prevState) => ({
        ...prevState,
        [filterType]: {
          ...prevState[filterType],
          selectedDonor: [],
          selectedFilters: [{ value: 'donor', label: 'Donor' }],
        },
      }));
      if (props?.onFiltersChange) {
        if (filterType === 'eore') {
          props.onFiltersChange({ selectedDonor: [] });
        } else {
          const completeCurrentFilters = props?.getCurrentCompleteFilters
            ? props.getCurrentCompleteFilters()
            : {};
          props.onFiltersChange({
            ...completeCurrentFilters,
            selectedDonor: [],
          });
        }
      }
    }
    setOpen(false);
  };

  useEffect(() => {
    if (props?.resetFiltersStatus !== true) return;
    setFilterStates((prev) => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        selectedDonor: [],
        selectedCustomer: [],
        selectedFilters: [{ value: 'donor', label: 'Donor' }],
      },
    }));
    setOpen(false);
  }, [props?.resetFiltersStatus, filterType]);

  const selectedDonorsCount = selectedValues?.selectedDonor?.length || 0;
  const selectedCustomersCount = selectedValues?.selectedCustomer?.length || 0;

  const openFilterDialog = (openedFrom) => (e) => {
    e.stopPropagation();
    handleClickOpen(openedFrom);
  };

  const dialogTitle = dialogOpenedFrom === 'customer' ? 'Customer' : 'Donor';

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <div className="text-center filter-container-with-badge">
        <ButtonComponent
          className="filter-btn"
          configuration="filled"
          state="enabled"
          iconRight={<FilterSvg />}
          label="Donor"
          handleButton={openFilterDialog('donor')}
        />
        {selectedDonorsCount > 0 && (
          <span className="filter-count-badge">{selectedDonorsCount}</span>
        )}
      </div>
      <div className="text-center filter-container-with-badge">
        <ButtonComponent
          className="filter-btn"
          configuration="filled"
          state="enabled"
          iconRight={<FilterSvg />}
          label="Customer"
          handleButton={openFilterDialog('customer')}
        />
        {selectedCustomersCount > 0 && (
          <span className="filter-count-badge">{selectedCustomersCount}</span>
        )}
      </div>
      <Dialog
        className="filter-dialog"
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="confirm-popup">
          <div className="header">
            <span className="title" id="alert-dialog-title">
              {dialogTitle}
            </span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>
          <div className="select-wrapper w-100" style={{ gap: '0' }}>
            <span className="label">{dialogTitle}</span>
            <div onClick={(e) => e.stopPropagation()}>
              <SelectComponent
                placeholder={`Select ${dialogTitle.toLowerCase()}...`}
                className="w-100 donor-popup-select"
                value={
                  dialogOpenedFrom === 'customer'
                    ? selectedValues?.selectedCustomer
                    : selectedValues?.selectedDonor
                }
                options={donorOptions}
                handleSelect={handleDialogValueSelection}
                isMulti
                isOptionDisabled={(option) => option?.isDisabled}
                customStyles={{
                  menu: (provided) => ({
                    ...provided,
                    position: 'static',
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    borderTop: 'none',
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: 'none',
                    padding: 0,
                  }),
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #868686',
                    borderRadius: '4px',
                    '&:hover': {
                      border: '1px solid #00b1b7',
                      cursor: 'pointer',
                    },
                  }),
                }}
              />
            </div>
          </div>

          <div className="buttons-group">
            <ButtonComponent
              iconLeft={<CancelSvg />}
              className="danger"
              handleButton={resetFilteredData}
              label="Reset"
            />
            <ButtonComponent iconLeft={<FilterSvg />} handleButton={applyFilters} label="Apply" />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export const FilterForClearanceDialog = (props) => {
  const [open, setOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [donorOptions, setDonorOptions] = useState([]);
  const [methodTypeOptions, setMethodTypeOptions] = useState([]);

  const getInitialClearanceValues = () => ({
    selectedFilters: [],
    selectedStatus: [],
    selectedDonors: [],
    selectedMethodTypes: [],
  });

  const [selectedValues, setSelectedValues] = useState(() => {
    if (props?.selectedValues && Object.keys(props.selectedValues).length > 0) {
      return {
        selectedFilters: props.selectedValues.selectedFilters || [],
        selectedStatus: props.selectedValues.selectedStatus || [],
        selectedDonors: props.selectedValues.selectedDonors || [],
        selectedMethodTypes: props.selectedValues.selectedMethodTypes || [],
      };
    }
    return getInitialClearanceValues();
  });

  useEffect(() => {
    if (props?.selectedValues && Object.keys(props.selectedValues).length > 0) {
      setSelectedValues({
        selectedFilters: props.selectedValues.selectedFilters || [],
        selectedStatus: props.selectedValues.selectedStatus || [],
        selectedDonors: props.selectedValues.selectedDonors || [],
        selectedMethodTypes: props.selectedValues.selectedMethodTypes || [],
      });
    } else {
      setSelectedValues(getInitialClearanceValues());
    }
  }, [props?.selectedValues]);

  const menuPortalHighZIndex = {
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menu: (base) => ({ ...base, zIndex: 2000 }),
  };

  const fetchValidationStatus = (type) =>
    getOptionsForClearance('validation_status')
      .then((response) => {
        const data = response?.data || [];
        if (data?.length === 0) {
          setStatusOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
        } else {
          setStatusOptions(data);
        }
      })
      .catch(() => {
        setStatusOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
      });

  const fetchDonorOptions = (type) =>
    getOptionsForClearance('donor')
      .then((response) => {
        const data = response?.data || [];
        if (data?.length === 0) {
          setDonorOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
        } else {
          setDonorOptions(data);
        }
      })
      .catch(() => {
        setDonorOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
      });

  const fetchMethodTypeOptions = (type) =>
    getOptionsForClearance('method')
      .then((response) => {
        const data = response?.data || [];
        if (data?.length === 0) {
          setMethodTypeOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
        } else {
          setMethodTypeOptions(data);
        }
      })
      .catch(() => {
        setMethodTypeOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
      });

  const handleClickOpen = () => {
    setOpen(true);
    fetchValidationStatus();
    fetchDonorOptions();
    fetchMethodTypeOptions();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFilterSelection = (selected) => {
    const newState = {
      ...selectedValues,
      selectedFilters: selected,
    };
    const selectedFilterValues = (selected || []).map((item) => item.value);

    if (!selectedFilterValues?.includes('validation_status')) {
      newState.selectedStatus = [];
    }
    if (!selectedFilterValues.includes('donor')) {
      newState.selectedDonors = [];
    }
    if (!selectedFilterValues.includes('method')) {
      newState.selectedMethodTypes = [];
    }

    setSelectedValues(newState);
  };

  const handleStatusSelection = (selected) => {
    setSelectedValues((prev) => ({
      ...prev,
      selectedStatus: selected || [],
    }));
  };

  const handleDonorSelection = (selected) => {
    setSelectedValues((prev) => ({
      ...prev,
      selectedDonors: selected || [],
    }));
  };

  const handleMethodTypesSelection = (selected) => {
    setSelectedValues((prev) => ({
      ...prev,
      selectedMethodTypes: selected || [],
    }));
  };

  const resetFilteredData = () => {
    const emptyState = {
      selectedFilters: [],
      selectedStatus: [],
      selectedDonors: [],
      selectedMethodTypes: [],
    };

    setSelectedValues(emptyState);
    props.resetPage?.();
    props.onFiltersChange?.(emptyState);
    handleClose();
  };

  const applyFilters = () => {
    props.onFiltersChange?.(selectedValues);
    handleClose();
  };

  const filterOptions = [
    { value: 'validation_status', label: 'Status' },
    { value: 'donor', label: 'Donor' },
    { value: 'method', label: 'Method Type' },
  ];

  const selectedFiltersCount =
    (selectedValues.selectedStatus?.length || 0) +
    (selectedValues.selectedDonors?.length || 0) +
    (selectedValues.selectedMethodTypes?.length || 0);

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      position: 'absolute',
      zIndex: 9999,
      width: '100%',
      top: '100%',
      left: 0,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      position: 'relative',
    }),
    container: (provided) => ({
      ...provided,
      position: 'relative',
    }),
  };

  return (
    <div className="text-center filter-container-with-badge">
      <ButtonComponent
        className="filter-btn"
        configuration="filled"
        state="enabled"
        iconRight={<FilterSvg />}
        label="Filter"
        handleButton={handleClickOpen}
      />
      {selectedFiltersCount > 0 && (
        <span className="filter-count-badge">{selectedFiltersCount}</span>
      )}
      <Dialog
        className="filter-dialog"
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        style={{ overflow: 'visible' }}
        PaperProps={{
          style: { overflow: 'visible' },
        }}
      >
        <div className="confirm-popup">
          <div className="header">
            <span className="title">Filter</span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>
          <div className="select-wrapper w-100" style={{ gap: '0' }}>
            <span className="label">Select filters</span>
            <SelectComponent
              placeholder="Select..."
              className="w-100"
              value={selectedValues?.selectedFilters}
              options={
                props.activeTabIndex !== 0
                  ? [
                      { value: 'validation_status', label: 'Status' },
                      { value: 'internalId', label: 'Internal Id' },
                      { value: 'reporterName', label: 'Reporter name' },
                    ]
                  : [
                      { value: 'validation_status', label: 'Status' },
                      { value: 'donor', label: 'Donor' },
                      { value: 'method', label: 'Method type' },
                    ]
              }
              handleSelect={handleFilterSelection}
              isMulti
            />
          </div>

          {/* {selectedValues?.selectedFilters?.some((item) => item.value === 'internalId')
            && props.activeTabIndex === 1 && (
              <div className="select-wrapper w-100" style={{ gap: '0' }}>
                <span className="label">Select Internal Id</span>
                <SelectComponent
                  placeholder="Select..."
                  className="w-100"
                  value={selectedValues?.selectedInternalIds}
                  options={[
                    { value: 'I3T|SHA', label: 'I3T|SHA' },
                    { value: '3T|CHA', label: '3T|CHA' },
                  ]}
                  handleSelect={handleInternalIdSelection}
                  isMulti
                />
              </div>
            )} */}
          {/* {selectedValues?.selectedFilters?.some((item) => item.value === 'reporterName')
            && props.activeTabIndex === 1 && (
              <div className="select-wrapper w-100" style={{ gap: '0' }}>
                <span className="label">Select Reporter name</span>
                <SelectComponent
                  placeholder="Select..."
                  className="w-100"
                  value={selectedValues?.selectedReporterNames}
                  options={[
                    { value: 'I3T|SHA', label: 'I3T|SHA' },
                    { value: '3T|CHA', label: '3T|CHA' },
                  ]}
                  handleSelect={handleReporterNamesSelection}
                  isMulti
                />
              </div>
            )} */}
          {selectedValues?.selectedFilters?.some((item) => item.value === 'validation_status') && (
            <div className="select-wrapper w-100" style={{ gap: '0' }}>
              <span className="label">Select status</span>
              <SelectComponent
                placeholder="Select..."
                className="filter-status"
                value={selectedValues?.selectedStatus}
                options={statusOptions}
                handleSelect={handleStatusSelection}
                isMulti
                menuPortalTarget={document.body}
                isOptionDisabled={(option) => option?.isDisabled}
                customStyles={menuPortalHighZIndex}
              />
            </div>
          )}
          {selectedValues?.selectedFilters?.some((item) => item.value === 'donor') && (
            <div className="select-wrapper w-100" style={{ gap: '0' }}>
              <span className="label">Select donor</span>
              <SelectComponent
                placeholder="Select..."
                className="filter-status"
                value={selectedValues?.selectedDonors}
                options={donorOptions}
                handleSelect={handleDonorSelection}
                isMulti
                menuPortalTarget={document.body}
                isOptionDisabled={(option) => option?.isDisabled}
                customStyles={menuPortalHighZIndex}
              />
            </div>
          )}
          {selectedValues?.selectedFilters?.some((item) => item?.value === 'method') && (
            <div className="select-wrapper w-100" style={{ gap: '0' }}>
              <span className="label">Select method type</span>
              <SelectComponent
                placeholder="Select..."
                className="filter-status"
                value={selectedValues?.selectedMethodTypes}
                options={methodTypeOptions}
                handleSelect={handleMethodTypesSelection}
                isMulti
                menuPortalTarget={document.body}
                isOptionDisabled={(option) => option?.isDisabled}
                customStyles={menuPortalHighZIndex}
              />
            </div>
          )}
          <div className="buttons-group">
            <ButtonComponent
              iconLeft={<CancelSvg />}
              className="danger"
              handleButton={resetFilteredData}
              label="Reset"
            />
            <ButtonComponent iconLeft={<FilterSvg />} handleButton={applyFilters} label="Apply" />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export const FilterForDailyAndWeeklyDialog = (props) => {
  const [open, setOpen] = useState(false);
  const [haReportOptions, setHaReportOptions] = useState([]);
  const [clearanceTaskOptions, setClearanceTaskOptions] = useState([]);
  const [haReportInputValue, setHaReportInputValue] = useState('');
  const [clearanceTaskInputValue, setClearanceTaskInputValue] = useState('');

  const getInitialValues = () => ({
    selectedFilters: [],
    filters: {
      haReports: props.type === 'weekly' ? [] : [],
      clearanceTasks: [],
    },
    selectedFields: [],
  });

  const findLabelForValue = (value, options) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const [selectedValues, setSelectedValues] = useState(() => {
    if (props.selectedValues && Object.keys(props.selectedValues).length > 0) {
      return {
        selectedFilters: Array.isArray(props.selectedValues.selectedFilters)
          ? props.selectedValues.selectedFilters.map((filter) =>
              typeof filter === 'string'
                ? {
                    value: filter,
                    label: filter === 'ha_reports' ? 'HA Reports' : 'Clearance Task Reports',
                  }
                : filter
            )
          : [],
        filters: {
          haReports: Array.isArray(props.selectedValues.filters?.haReports)
            ? props.selectedValues.filters.haReports.map((item) =>
                typeof item === 'string'
                  ? { value: item, label: findLabelForValue(item, haReportOptions) }
                  : item
              )
            : [],
          clearanceTasks: Array.isArray(props.selectedValues.filters?.clearanceTasks)
            ? props.selectedValues.filters.clearanceTasks.map((item) =>
                typeof item === 'string'
                  ? { value: item, label: findLabelForValue(item, clearanceTaskOptions) }
                  : item
              )
            : [],
        },
        selectedFields: props.selectedValues.selectedFields || [],
      };
    }
    return getInitialValues();
  });

  useEffect(() => {
    if (props.selectedValues && Object.keys(props.selectedValues).length > 0) {
      const formattedValues = {
        selectedFilters: Array.isArray(props.selectedValues.selectedFilters)
          ? props.selectedValues.selectedFilters.map((filter) =>
              typeof filter === 'string'
                ? {
                    value: filter,
                    label: filter === 'ha_reports' ? 'HA Reports' : 'Clearance Task Reports',
                  }
                : filter
            )
          : [],
        filters: {
          haReports: Array.isArray(props.selectedValues.filters?.haReports)
            ? props.selectedValues.filters.haReports.map((item) =>
                typeof item === 'string'
                  ? { value: item, label: findLabelForValue(item, haReportOptions) }
                  : item
              )
            : [],
          clearanceTasks: Array.isArray(props.selectedValues.filters?.clearanceTasks)
            ? props.selectedValues.filters.clearanceTasks.map((item) =>
                typeof item === 'string'
                  ? { value: item, label: findLabelForValue(item, clearanceTaskOptions) }
                  : item
              )
            : [],
        },
        selectedFields: props.selectedValues.selectedFields || [],
      };
      setSelectedValues(formattedValues);
    } else {
      // Якщо props.selectedValues пустий, скидаємо до початкових значень
      setSelectedValues(getInitialValues());
    }
  }, [props.selectedValues, props.type]);

  // Оновлюємо labels після завантаження опцій
  useEffect(() => {
    if (haReportOptions.length > 0 || clearanceTaskOptions.length > 0) {
      setSelectedValues((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          haReports: prev.filters.haReports.map((item) => ({
            ...item,
            label: findLabelForValue(item.value, haReportOptions) || item.label,
          })),
          clearanceTasks: prev.filters.clearanceTasks.map((item) => ({
            ...item,
            label: findLabelForValue(item.value, clearanceTaskOptions) || item.label,
          })),
        },
      }));
    }
  }, [haReportOptions, clearanceTaskOptions]);

  const filterOptions =
    props.type === 'weekly'
      ? [{ value: 'clearance_tasks', label: 'Clearance Task Reports' }]
      : [
          { value: 'ha_reports', label: 'HA Reports' },
          { value: 'clearance_tasks', label: 'Clearance Task Reports' },
        ];

  const fetchHaReportOptions = async () => {
    try {
      const response = await getHaReportChoices();
      const data = response?.data || [];

      if (data.length === 0) {
        setHaReportOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
      } else {
        setHaReportOptions(data);
      }
    } catch (error) {
      setHaReportOptions([{ value: '', label: 'Помилка завантаження', isDisabled: true }]);
    }
  };

  const fetchClearanceTaskOptions = async () => {
    try {
      const apiFunction =
        props.type === 'daily' ? getClearanceTaskChoices : getClearanceTaskChoicesForWeekly;
      const response = await apiFunction();
      const data = response?.data || [];

      if (data.length === 0) {
        setClearanceTaskOptions([{ value: '', label: 'Немає даних', isDisabled: true }]);
      } else {
        setClearanceTaskOptions(data);
      }
    } catch (error) {
      setClearanceTaskOptions([{ value: '', label: 'Помилка завантаження', isDisabled: true }]);
    }
  };

  const loadHaReportOptions = async (searchQuery, { page }) => {
    try {
      const query = searchQuery || haReportInputValue;

      if (!query || query.trim() === '') {
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 },
        };
      }

      const response = await getHaReportChoices({
        search: query,
        page: page || 1,
      });

      const data = response?.data?.results || response?.data || [];
      const hasMore = response?.data?.next ? true : false;

      return {
        options: data,
        hasMore: hasMore,
        additional: { page: (page || 1) + 1 },
      };
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  };

  const loadClearanceTaskOptions = async (searchQuery, { page }) => {
    try {
      const query = searchQuery || clearanceTaskInputValue;

      if (!query || query.trim() === '') {
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 },
        };
      }

      const apiFunction =
        props.type === 'daily' ? getClearanceTaskChoices : getClearanceTaskChoicesForWeekly;
      const response = await apiFunction({
        search: query,
        page: page || 1,
      });

      const data = response?.data?.results || response?.data || [];
      const hasMore = response?.data?.next ? true : false;

      return {
        options: data,
        hasMore: hasMore,
        additional: { page: (page || 1) + 1 },
      };
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
  };

  const handleHaReportInputChange = (newValue) => {
    setHaReportInputValue(newValue);
    return newValue;
  };

  const handleClearanceTaskInputChange = (newValue) => {
    setClearanceTaskInputValue(newValue);
    return newValue;
  };

  const handleClickOpen = () => {
    setOpen(true);

    // Завантажуємо choices тільки при відкритті попапу
    if (props.type !== 'weekly') {
      fetchHaReportOptions();
    }
    fetchClearanceTaskOptions();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFilterSelection = (selected) => {
    const newState = {
      ...selectedValues,
      selectedFilters: selected || [],
    };

    const selectedFilterValues = (selected || []).map((item) => item.value);

    if (!selectedFilterValues.includes('ha_reports') || props.type === 'weekly') {
      newState.filters.haReports = [];
    }
    if (!selectedFilterValues.includes('clearance_tasks')) {
      newState.filters.clearanceTasks = [];
    }

    setSelectedValues(newState);
  };

  const handleHaReportsSelection = (selected) => {
    setSelectedValues((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        haReports: selected || [],
      },
    }));
  };

  const handleClearanceTasksSelection = (selected) => {
    setSelectedValues((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        clearanceTasks: selected || [],
      },
    }));
  };

  const resetFilteredData = () => {
    const emptyState = {
      selectedFilters: [],
      filters: {
        haReports: [],
        clearanceTasks: [],
      },
      selectedFields: [],
    };

    setSelectedValues(emptyState);
    props.resetPage?.();
    props.onFiltersChange?.(emptyState);
    handleClose();
  };

  const applyFilters = () => {
    const filters = {
      selectedFilters: selectedValues.selectedFilters.map((item) => item.value),
      filters: {
        haReports: selectedValues.filters.haReports.map((item) => item.value),
        clearanceTasks: selectedValues.filters.clearanceTasks.map((item) => item.value),
      },
      selectedFields: [
        ...selectedValues.filters.haReports.map((item) => ({
          type: 'ha_reports',
          value: item.value,
        })),
        ...selectedValues.filters.clearanceTasks.map((item) => ({
          type: 'clearance_tasks',
          value: item.value,
        })),
      ],
    };

    props.onFiltersChange?.(filters);
    handleClose();
  };

  const selectedFiltersCount =
    (selectedValues.filters?.haReports?.length || 0) +
    (selectedValues.filters?.clearanceTasks?.length || 0);

  const selectStyles = {
    container: (base) => ({
      ...base,
      position: 'relative',
    }),
    menu: (provided) => ({
      ...provided,
      position: 'absolute',
      zIndex: 9999,
      width: '100%',
      top: '100%',
      left: 0,
      maxHeight: '200px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      overflowY: 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const asyncSelectStyles = {
    container: (base) => ({
      ...base,
      position: 'relative',
    }),
    control: (provided, state) => ({
      ...provided,
      border: '1px solid #868686',
      borderRadius: '4px',
      borderColor: state.isFocused ? '#00B1B7' : '#868686',
      boxShadow: state.isFocused ? 'none' : provided.boxShadow,
      '&:hover': {
        borderColor: '#00B1B7',
        cursor: 'pointer',
      },
    }),
    menu: (provided) => ({
      ...provided,
      position: 'absolute',
      zIndex: 9999,
      width: '100%',
      top: '100%',
      left: 0,
      maxHeight: '200px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      overflowY: 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#00B1B7'
        : state.isFocused
          ? '#DFDFDF'
          : provided.backgroundColor,
      color: state.isSelected ? 'white' : provided.color,
    }),
  };

  return (
    <div className="text-center filter-container-with-badge">
      <ButtonComponent
        className="filter-btn"
        configuration="filled"
        state="enabled"
        iconRight={<FilterSvg />}
        label="Filter"
        handleButton={(e) => {
          e.stopPropagation();
          handleClickOpen();
        }}
      />
      {selectedFiltersCount > 0 && (
        <span className="filter-count-badge">{selectedFiltersCount}</span>
      )}
      <Dialog
        className="filter-dialog"
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        style={{ overflow: 'visible' }}
        PaperProps={{
          style: { overflow: 'visible' },
        }}
      >
        <div className="confirm-popup">
          <div className="header">
            <span className="title">Filter</span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>

          <div className="select-wrapper w-100" style={{ gap: '0' }}>
            <span className="label">Select filters</span>
            <SelectComponent
              placeholder="Select..."
              className="w-100"
              value={selectedValues.selectedFilters}
              options={filterOptions}
              handleSelect={handleFilterSelection}
              isMulti
              styles={selectStyles}
            />
          </div>

          {selectedValues.selectedFilters?.some((f) => f.value === 'ha_reports') && (
            <div className="filter-group">
              <label>HA Reports</label>
              <AsyncPaginate
                isMulti
                loadOptions={loadHaReportOptions}
                value={selectedValues.filters.haReports}
                onChange={handleHaReportsSelection}
                onInputChange={handleHaReportInputChange}
                className="custom-select-container filter-select"
                additional={{ page: 1 }}
                placeholder="Enter"
                cacheOptions={false}
                defaultOptions={false}
                isClearable={true}
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'Nothing found' : 'Enter text to search'
                }
                styles={asyncSelectStyles}
              />
            </div>
          )}
          {selectedValues.selectedFilters?.some((f) => f.value === 'clearance_tasks') && (
            <div className="filter-group">
              <label>Clearance Task Reports</label>
              <AsyncPaginate
                isMulti
                loadOptions={loadClearanceTaskOptions}
                value={selectedValues.filters.clearanceTasks}
                onChange={handleClearanceTasksSelection}
                onInputChange={handleClearanceTaskInputChange}
                className="custom-select-container filter-select"
                additional={{ page: 1 }}
                placeholder="Enter"
                cacheOptions={false}
                defaultOptions={false}
                isClearable={true}
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'Nothing found' : 'Enter text to search'
                }
                styles={asyncSelectStyles}
              />
            </div>
          )}
          <div className="buttons-group">
            <ButtonComponent
              iconLeft={<CancelSvg />}
              className="danger"
              handleButton={resetFilteredData}
              label="Reset"
            />
            <ButtonComponent iconLeft={<FilterSvg />} handleButton={applyFilters} label="Apply" />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
